// src/pages/qbo/UnconvertCard.tsx
//
// 3.18.15 — undo the conversion of a cutoff EXCEPTION day. An exception day
// (Qbo:CutoffExceptions) has no legacy summary journal, so its documents
// belong in QuickBooks in full. If they were converted anyway (the setting
// was added later, or was unreadable), this rewrites each stub with its full
// lines + COGS journal, exactly as the Invoices push does, then deletes the
// day's offset journal(s). The agent refuses any day that is not an exception.

import { useEffect, useState } from 'react';
import { AlertTriangle, Undo2 } from 'lucide-react';
import qboApi from '../../services/qboApi';
import type { SyncResult, UnconvertPlan } from './qboTypes';
import { money } from './qboTypes';
import { runJob, type JobView } from './qboJobs';
import { fmtLocalTime } from '../../lib/formatTime';

interface Props {
  days: string[];
  onMessage: (type: 'error' | 'success' | 'info', text: string) => void;
}

interface Detail { invoice?: string; offset?: string; customer?: string; type?: string; total?: number; qboId?: string; cogsJournalId?: string; action?: string; status: string; error?: string }

export default function UnconvertCard({ days, onMessage }: Props) {
  const [day, setDay] = useState(days[0] || '');
  const [plan, setPlan] = useState<UnconvertPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState<JobView | null>(null);
  const [result, setResult] = useState<SyncResult | null>(null);

  useEffect(() => { if (!days.includes(day)) setDay(days[0] || ''); }, [days, day]);

  const load = async () => {
    if (!day) return;
    setLoading(true);
    setResult(null);
    try {
      setPlan((await qboApi.conversionUnconvertPlan(day)) as UnconvertPlan);
    } catch (e: unknown) {
      onMessage('error', `Undo conversion preview: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const run = async () => {
    setConfirming(false);
    setRunning(true);
    setResult(null);
    try {
      const res = (await runJob(() => qboApi.conversionUnconvertRun(day), setJob)) as SyncResult;
      setResult(res);
      onMessage(res.errors ? 'error' : 'success', `Undo conversion of ${day}: ${res.updated} document(s) rewritten in full, ${res.created} offset journal(s) deleted, ${res.errors} error(s)`);
      await load();
    } catch (e: unknown) {
      onMessage('error', `Undo conversion: ${(e as Error).message}`);
    } finally {
      setRunning(false);
      setJob(null);
    }
  };

  const work = plan ? plan.documents.length + plan.offsets.length : 0;

  return (
    <div className="bg-slate-800 rounded-xl p-5 border border-amber-900/60 mb-5">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div>
          <div className="text-sm font-semibold text-slate-400 flex items-center gap-2"><Undo2 size={14} /> Undo conversion of an exception day</div>
          <div className="text-xs text-slate-500 mt-1 max-w-3xl">
            No legacy journal carries the sales of {days.join(', ')}, so documents dated that day belong in QuickBooks in full. If they were converted
            (stub at open balance + offset journal) before the exception was configured, this rewrites each stub with its full lines and COGS journal,
            as the Invoices push does, then deletes the day's offset journal(s). Nothing changes until you confirm.
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {days.length > 1 && (
            <select value={day} onChange={(e) => { setDay(e.target.value); setPlan(null); }} className="bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-sm text-slate-200">
              {days.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          <button
            className="px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
            onClick={load}
            disabled={loading || running || !day}
          >
            {loading ? 'Loading…' : plan ? 'Reload' : `Preview ${day}`}
          </button>
          {plan && plan.exceptionDay && work > 0 && !confirming && (
            <button
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-700 hover:bg-amber-600 text-white transition-colors disabled:opacity-50"
              onClick={() => setConfirming(true)}
              disabled={running}
            >
              Undo conversion…
            </button>
          )}
        </div>
      </div>

      {confirming && plan && (
        <div className="mt-3 p-3 rounded-lg border border-amber-700 bg-amber-950/40 text-sm text-amber-100 flex flex-wrap items-center gap-3">
          <AlertTriangle size={16} />
          <span>
            Rewrite <b>{plan.documents.length}</b> document(s) ({money(plan.fullTotal)} in full, {money(plan.openTotal)} as stubs now) and delete <b>{plan.offsets.length}</b> offset journal(s) for {plan.day}?
          </span>
          <button className="px-3 py-1 rounded bg-amber-600 hover:bg-amber-500 text-white" onClick={run}>Yes, undo</button>
          <button className="px-3 py-1 rounded border border-slate-500 text-slate-300" onClick={() => setConfirming(false)}>Cancel</button>
        </div>
      )}

      {running && (
        <div className="mt-3 text-sm text-slate-300">
          Working… {job ? `${job.done} / ${job.total}` : ''}
          <div className="h-1.5 bg-slate-700 rounded mt-1 overflow-hidden"><div className="h-full bg-amber-500 transition-all" style={{ width: job && job.total ? `${Math.min(100, (100 * job.done) / job.total)}%` : '5%' }} /></div>
        </div>
      )}

      {plan && (
        <div className="mt-3 text-xs">
          {!plan.exceptionDay && <div className="text-red-300 mb-2">{plan.notes[0]}</div>}
          {plan.exceptionDay && (
            <div className="flex flex-wrap gap-4 text-slate-300 mb-2">
              <span>Converted documents: <b className="text-slate-100">{plan.documents.length}</b></span>
              <span>As stubs now: <b className="text-slate-100">{money(plan.openTotal)}</b></span>
              <span>In full: <b className="text-slate-100">{money(plan.fullTotal)}</b></span>
              <span>Offset journals: <b className="text-slate-100">{plan.offsets.length}</b></span>
            </div>
          )}
          {plan.offsets.length > 0 && (
            <div className="text-slate-400 mb-2">
              Offset journal(s): {plan.offsets.map((o) => <span key={o.key} className="font-mono mr-2">{o.key} → QBO {o.qboId}{o.at ? ` (${fmtLocalTime(o.at)})` : ''}</span>)}
            </div>
          )}
          {plan.documents.length > 0 && (
            <div className="overflow-x-auto max-h-72 overflow-y-auto border border-slate-700 rounded-lg">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-900 text-slate-400">
                  <tr><th className="text-left p-1.5">Invoice</th><th className="text-left p-1.5">Customer</th><th className="text-right p-1.5">Total</th><th className="text-right p-1.5">Stub (open)</th><th className="text-left p-1.5">QBO</th><th className="text-left p-1.5">Converted</th></tr>
                </thead>
                <tbody>
                  {plan.documents.map((d) => (
                    <tr key={d.invoice} className="border-t border-slate-700/60 text-slate-200">
                      <td className="p-1.5 font-mono">{d.invoice}</td>
                      <td className="p-1.5">{d.customer}</td>
                      <td className="p-1.5 text-right">{money(d.total)}</td>
                      <td className="p-1.5 text-right">{money(d.open)}</td>
                      <td className="p-1.5 font-mono text-slate-400">{d.qboType} {d.qboId}</td>
                      <td className="p-1.5 text-slate-400">{d.at ? fmtLocalTime(d.at) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {plan.notes.filter((_, i) => plan.exceptionDay || i > 0).map((n, i) => <div key={i} className="text-slate-500 mt-2">{n}</div>)}
        </div>
      )}

      {result && (
        <div className="mt-3 text-xs">
          <div className="text-slate-300 mb-1">Result: {result.updated} rewritten, {result.created} offset(s) deleted, {result.errors} error(s) · {result.duration}</div>
          <ul className="space-y-0.5">
            {(result.details as Detail[]).map((d, i) => (
              <li key={i} className={d.status === 'error' ? 'text-red-300' : 'text-slate-400'}>
                <span className="font-mono">{d.invoice ?? d.offset}</span> {d.customer ? `· ${d.customer} ` : ''}{d.action ? `· ${d.action}` : ''}{d.qboId ? ` · QBO ${d.qboId}` : ''}{d.cogsJournalId ? ` · COGS journal ${d.cogsJournalId}` : ''}{d.error ? ` — ${d.error}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
